/**
 * ActivityService — API client for activity tracking endpoints.
 * Records what users watched/played/read and with whom.
 */

function getApiBase() {
    const proxyUrl = typeof import.meta !== 'undefined' && import.meta.env?.VITE_PROXY_URL;
    return proxyUrl ? `${proxyUrl.replace(/\/+$/, '')}/api` : '/api';
}

export default class ActivityService {
    constructor(trackerUserId, getToken) {
        this._userId = trackerUserId;
        this._getToken = getToken;
        this._apiBase = getApiBase();
    }

    async _headers(json = false) {
        const h = { 'x-tracker-user-id': this._userId };
        if (this._getToken) {
            try {
                const token = await this._getToken();
                if (token) h['Authorization'] = `Bearer ${token}`;
            } catch (_) { /* fall back to x-tracker-user-id */ }
        }
        if (json) h['Content-Type'] = 'application/json';
        return h;
    }

    /**
     * Create a new activity record.
     * @param {{ type: string, itemType: string, itemId: string, participants: string[], pod?: string, date?: string, notes?: string }} activity
     */
    async create(activity) {
        const res = await fetch(`${this._apiBase}/activities`, {
            method: 'POST',
            headers: await this._headers(true),
            body: JSON.stringify(activity),
        });
        if (!res.ok) {
            const data = await res.json().catch(() => ({}));
            throw new Error(data.error || `Failed to create activity: ${res.status}`);
        }
        return res.json();
    }

    /**
     * List activities for the user's pods.
     * @param {{ podId?: string, limit?: number, offset?: number }} params
     */
    async list(params = {}) {
        const qs = new URLSearchParams();
        if (params.podId) qs.set('podId', params.podId);
        if (params.limit) qs.set('limit', String(params.limit));
        if (params.offset) qs.set('offset', String(params.offset));
        if (params.itemTypes?.length) qs.set('itemTypes', params.itemTypes.join(','));
        const res = await fetch(`${this._apiBase}/activities?${qs}`, {
            headers: await this._headers(),
        });
        if (!res.ok) {
            const data = await res.json().catch(() => ({}));
            throw new Error(data.error || `Failed to fetch activities: ${res.status}`);
        }
        return res.json();
    }

    /**
     * Build a map of itemId → co-participant user IDs for a given item type.
     * Returns { [itemId]: string[] } where the array contains user IDs
     * who co-logged the activity WITH the current user.
     * @param {string} itemType - 'game', 'movie', 'book', 'boardgame', 'season'
     * @returns {Promise<Object>} Map of itemId to co-participant user IDs
     */
    async getCoParticipantMap(itemType) {
        try {
            const map = {};
            const PAGE_SIZE = 500;
            let offset = 0;
            let total = Infinity;

            // Paginate through all activities
            while (offset < total) {
                const result = await this.list({ limit: PAGE_SIZE, offset });
                total = result.totalItems ?? 0;
                const items = result.items || [];
                if (items.length === 0) break;

                for (const a of items) {
                    if (a.itemType !== itemType) continue;
                    if (!a.participants?.includes(this._userId)) continue;
                    const others = a.participants.filter(id => id !== this._userId);
                    if (others.length > 0) {
                        if (!map[a.itemId]) map[a.itemId] = new Set();
                        for (const id of others) map[a.itemId].add(id);
                    }
                }
                offset += items.length;
            }

            // Convert sets to arrays
            for (const key of Object.keys(map)) {
                map[key] = [...map[key]];
            }
            return map;
        } catch {
            // Activity data is supplementary — don't block collection loading
            return {};
        }
    }

    /**
     * Build co-participant maps for multiple item types in a single pagination pass.
     * More efficient than calling getCoParticipantMap() once per type.
     * @param {string[]} itemTypes - e.g. ['movie', 'season']
     * @returns {Promise<Object>} { [itemType]: { [itemId]: string[] } }
     */
    async getCoParticipantMaps(itemTypes) {
        try {
            const maps = Object.fromEntries(itemTypes.map(t => [t, {}]));
            const PAGE_SIZE = 500;
            let offset = 0;
            let total = Infinity;

            while (offset < total) {
                const result = await this.list({ limit: PAGE_SIZE, offset, itemTypes });
                total = result.totalItems ?? 0;
                const items = result.items || [];
                if (items.length === 0) break;

                for (const a of items) {
                    if (!maps[a.itemType]) continue;
                    if (!a.participants?.includes(this._userId)) continue;
                    const others = a.participants.filter(id => id !== this._userId);
                    if (others.length > 0) {
                        if (!maps[a.itemType][a.itemId]) maps[a.itemType][a.itemId] = new Set();
                        for (const id of others) maps[a.itemType][a.itemId].add(id);
                    }
                }
                offset += items.length;
            }

            // Convert sets to arrays
            for (const type of itemTypes) {
                for (const key of Object.keys(maps[type])) {
                    maps[type][key] = [...maps[type][key]];
                }
            }
            return maps;
        } catch {
            return Object.fromEntries(itemTypes.map(t => [t, {}]));
        }
    }

    /**
     * Delete an activity record (only creator can delete).
     * @param {string} id
     */
    async delete(id) {
        const res = await fetch(`${this._apiBase}/activities/${id}`, {
            method: 'DELETE',
            headers: await this._headers(),
        });
        if (!res.ok) {
            const data = await res.json().catch(() => ({}));
            throw new Error(data.error || `Failed to delete activity: ${res.status}`);
        }
        return res.json();
    }
}
